/**
 * BNS-3167 Task 5: question-entry validation for the duplicate detail flow.
 *
 * The create-questions payload (buildExamPayload in BuatUjian) maps every
 * question entry down to exactly these five qbank identifiers when the copy is
 * submitted via addUjian/addUjianBulk. An entry missing any of them would hand
 * the backend a broken question shell — or silently drop question content in a
 * duplicate copy. So before the exam detail + questions may be dispatched /
 * navigated for duplication, EVERY normalized question entry must carry all
 * five identifiers. A single malformed entry rejects the whole load (no
 * dispatch, no navigation, no silent partial duplication).
 *
 * Re-review (BNS-3167 Task 5 gap): the create-payload contract itself allows
 * an UNGROUPED question to submit `qbank_group_id: null` (see the
 * buildExamPayload spec). Only qbank_quiz_id / qbank_question_id / qbank_point
 * / qbank_question_type are required non-null; qbank_group_id is the single
 * nullable identifier.
 *
 * Final re-review (BNS-3167 Task 5 validation): qbank_group_id must still be
 * EXPLICITLY present on the entry — an ungrouped question is encoded as an
 * explicit `null`, so an entry whose qbank_group_id key is missing or carries
 * `undefined` is malformed, exactly like any other missing identifier. Only an
 * explicit `null` value is the legal ungrouped shape.
 */
export const REQUIRED_QUESTION_IDENTIFIERS = [
  'qbank_quiz_id',
  'qbank_question_id',
  'qbank_group_id',
  'qbank_point',
  'qbank_question_type',
];

/** Identifiers the create payload maps that must carry a real (non-null) value. */
export const REQUIRED_NON_NULL_QUESTION_IDENTIFIERS = [
  'qbank_quiz_id',
  'qbank_question_id',
  'qbank_point',
  'qbank_question_type',
];

/** Identifiers the create payload maps that may legitimately be null (ungrouped questions). */
export const NULLABLE_QUESTION_IDENTIFIERS = ['qbank_group_id'];

/**
 * True when a single (already normalized) question entry carries every
 * identifier the create flow needs.
 *
 * - Every identifier in REQUIRED_QUESTION_IDENTIFIERS must be EXPLICITLY
 *   present: `undefined` (an absent key OR an explicit undefined value) counts
 *   as missing. qbank_group_id is included here, so an entry without the key —
 *   or with an undefined value — is malformed.
 * - qbank_point may legitimately be 0, so only `null` counts as "no real
 *   value" for the required non-null fields.
 * - qbank_group_id is the single nullable identifier: an explicit `null`
 *   (an ungrouped question) is a legal create-payload value and is exempt from
 *   the non-null requirement — but it must still be present.
 */
export const isNormalizedQuestionValid = (entry) => {
  if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) {
    return false;
  }
  const everyIdentifierPresent = REQUIRED_QUESTION_IDENTIFIERS.every((key) => entry[key] !== undefined);
  if (!everyIdentifierPresent) {
    return false;
  }
  return REQUIRED_NON_NULL_QUESTION_IDENTIFIERS.every((key) => entry[key] !== null);
};

/**
 * True only when EVERY entry of the normalized questions array is valid.
 * An empty array is a valid (empty) result — a source exam may genuinely have
 * zero questions — while anything that is not an array fails the load.
 */
export const allQuestionsValid = (questions) => Array.isArray(questions) && questions.every(isNormalizedQuestionValid);

export default allQuestionsValid;
